import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import api, { setUnauthorizedHandler } from "@/lib/api";

export interface AuthUser {
  id: number;
  email: string;
  display_name: string | null;
  role: string;
  status: string;
  /** Agents-console visibility (per-user flag; nav hides without it). */
  can_view_agents?: boolean;
  /** Sidebar pin order (route paths). Undefined/null = never
   *  customized; the sidebar seeds its defaults. */
  sidebar_pinned?: string[] | null;
  /** Weekly Update send list the user last used (prefilled next time).
   *  Undefined/null = never sent to a list. */
  weekly_update_recipients?: string[] | null;
  /** Color theme: "light" | "dark" | "system". Undefined/null = never
   *  chosen → Light. See lib/theme.ts. */
  theme?: string | null;
}

type AuthStatus = "loading" | "authed" | "anon";

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: AuthUser) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);

  // On boot, ask the backend who we are. 200 → authed; 401 → anon.
  useEffect(() => {
    let cancelled = false;
    api
      .get<AuthUser>("/auth/me")
      .then(({ data }) => {
        if (cancelled) return;
        setUser(data);
        setStatus("authed");
      })
      .catch(() => {
        if (cancelled) return;
        setUser(null);
        setStatus("anon");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Any 401 from a protected call (e.g. session expired mid-session)
  // flips the whole app back to the login screen.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      // Leave a note for the login screen so a mid-session expiry reads
      // as "your session expired", not a silent dump to the login page.
      try {
        sessionStorage.setItem("rp.session_expired", "1");
      } catch { /* ignore */ }
      setUser(null);
      setStatus("anon");
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { data } = await api.post<AuthUser>("/auth/login", { email, password });
    setUser(data);
    setStatus("authed");
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Even if the server call fails, drop the local session.
    }
    setUser(null);
    setStatus("anon");
  }, []);

  const updateUser = useCallback((next: AuthUser) => setUser(next), []);

  return (
    <AuthContext.Provider value={{ status, user, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
